// Describe el beneficio de las palancas en una línea, como el mock: «$500 off equipo + 3× $50 tiempo
// aire + Regalo: Funda X + $300 Wallet Fiado». Lee lo tecleado; las piezas sin valor no se nombran.
import { ACCESSORY_SUBCATEGORY_LABEL } from './salePromotionFormOptions';
import type { CappedDiscountForm, SalePromotionFormValues } from './salePromotionForm';

export const NO_LEVERS_TEXT = 'Sin palancas configuradas';

const MONEY = /^\d+(\.\d{1,2})?$/;

const wholePesos = new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 0,
});
const withCents = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' });
const percentFormat = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 2 });

/** Pesos tecleados a «$1,500» o «$12.50»; `null` si no es un monto mayor a cero. */
function pesos(text: string): string | null {
    const trimmed = text.trim();
    if (!MONEY.test(trimmed) || Number(trimmed) <= 0) return null;
    const value = Number(trimmed);
    return (Number.isInteger(value) ? wholePesos : withCents).format(value);
}

function percent(text: string): string | null {
    const trimmed = text.trim();
    if (!MONEY.test(trimmed) || Number(trimmed) <= 0) return null;
    return `${percentFormat.format(Number(trimmed))}%`;
}

function count(text: string): number | null {
    const value = Number(text.trim());
    return Number.isInteger(value) && value >= 1 ? value : null;
}

function discountText(discount: { type: string; value: string }): string | null {
    return discount.type === 'PERCENTAGE' ? percent(discount.value) : pesos(discount.value);
}

/** «$500 off equipo» o «10% off equipo (tope $300)». */
function cappedDiscount(discount: CappedDiscountForm, target: string): string | null {
    if (!discount.enabled) return null;
    const value = discountText(discount);
    if (value === null) return null;
    const cap = pesos(discount.maxAmount);
    return `${value} off ${target}${cap ? ` (tope ${cap})` : ''}`;
}

function airtime(values: SalePromotionFormValues): string | null {
    if (!values.airtime.enabled) return null;
    const amount = pesos(values.airtime.rechargeAmount);
    if (amount === null) return null;
    const recharges = count(values.airtime.rechargeCount) ?? 1;
    return recharges > 1 ? `${String(recharges)}× ${amount} tiempo aire` : `${amount} tiempo aire`;
}

function gift(values: SalePromotionFormValues, productNames: ReadonlyMap<string, string>) {
    const { gift: lever } = values;
    if (!lever.enabled) return null;
    if (lever.mode === 'RANGE') {
        if (lever.accessorySubcategory === '') return null;
        const max = pesos(lever.maxPrice);
        const label = ACCESSORY_SUBCATEGORY_LABEL[lever.accessorySubcategory];
        return `Regalo: ${label}${max ? ` hasta ${max}` : ''}`;
    }
    if (lever.skus.length === 0) return null;
    if (lever.skus.length === 1) {
        const sku = lever.skus[0] ?? '';
        return `Regalo: ${productNames.get(sku) ?? sku}`;
    }
    const choose = count(lever.chooseCount) ?? 1;
    return `Regalo: elige ${String(choose)} de ${String(lever.skus.length)}`;
}

function coupon(values: SalePromotionFormValues): string | null {
    if (!values.coupon.enabled) return null;
    const value = discountText(values.coupon);
    return value === null ? null : `Cupón ${value} próxima compra`;
}

function walletBonus(values: SalePromotionFormValues): string | null {
    if (!values.walletBonus.enabled) return null;
    const amount = pesos(values.walletBonus.amount);
    return amount === null ? null : `${amount} Wallet Fiado`;
}

/** Las piezas van en el orden del mock; el cupón, que el mock no tiene, al final. */
export function describeLevers(
    values: SalePromotionFormValues,
    productNames: ReadonlyMap<string, string> = new Map(),
): string {
    const parts = [
        cappedDiscount(values.deviceDiscount, 'equipo'),
        airtime(values),
        gift(values, productNames),
        cappedDiscount(values.accessoryDiscount, 'accesorios'),
        walletBonus(values),
        coupon(values),
    ].filter((part): part is string => part !== null);
    return parts.length === 0 ? NO_LEVERS_TEXT : parts.join(' + ');
}
